import React, { useState, useEffect } from 'react';
import {
  BookOpen, Flame, Heart, Clock, Star, Trophy,
  TrendingUp, Eye, Award, Compass
} from 'lucide-react';
import axios from 'axios';

const API = `${window.location.origin}/api`;

const READER_TYPES = {
  'Deep Reader': { color: '#10B981', icon: Heart },
  'Engaged Reader': { color: '#3B82F6', icon: TrendingUp },
  'Casual Reader': { color: '#F59E0B', icon: BookOpen },
  'Explorer': { color: '#8B5CF6', icon: Compass },
};

export default function ReadingInsightsCard() {
  const [insights, setInsights] = useState(null);
  const token = localStorage.getItem('token');

  useEffect(() => {
    if (!token) return;
    const fetch = async () => {
      try {
        const res = await axios.get(`${API}/user-reading-insights`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setInsights(res.data);
      } catch {
        // Silent
      }
    };
    fetch();
  }, [token]);

  if (!insights || !token) return null;

  const { stats, patterns, reader_type } = insights;
  const typeConfig = READER_TYPES[reader_type] || READER_TYPES['Explorer'];
  const TypeIcon = typeConfig.icon;

  const statItems = [
    { icon: BookOpen, label: 'Completed', value: stats.books_completed, color: '#10B981' },
    { icon: TrendingUp, label: 'In Progress', value: stats.books_in_progress, color: '#3B82F6' },
    { icon: Flame, label: 'Streak', value: `${stats.current_streak}d`, color: '#EF4444' },
    { icon: Heart, label: 'Engagement', value: `${stats.avg_engagement_score}%`, color: '#EC4899' },
    { icon: Clock, label: 'Reading', value: `${stats.total_reading_hours}h`, color: '#F59E0B' },
    { icon: Star, label: 'Reviews', value: stats.reviews_written, color: '#8B5CF6' },
    { icon: Award, label: 'Badges', value: stats.badges_earned, color: '#F97316' },
    { icon: Compass, label: 'Genres', value: stats.genres_explored, color: '#14B8A6' },
  ];

  return (
    <div data-testid="reading-insights-card" className="bg-zinc-900/80 border border-zinc-800 rounded-xl overflow-hidden">
      {/* Header */}
      <div className="px-5 py-4 border-b border-zinc-800 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div
            className="w-10 h-10 rounded-full flex items-center justify-center"
            style={{ backgroundColor: `${typeConfig.color}20` }}
          >
            <TypeIcon className="w-5 h-5" style={{ color: typeConfig.color }} />
          </div>
          <div>
            <h3 className="text-sm font-bold text-white">Your Reading Insights</h3>
            <p className="text-xs font-medium" style={{ color: typeConfig.color }}>{reader_type}</p>
          </div>
        </div>
        {stats.longest_streak > 0 && (
          <div className="text-right">
            <p className="text-[10px] text-zinc-500 uppercase">Best Streak</p>
            <p className="text-sm font-bold text-amber-400">{stats.longest_streak} days</p>
          </div>
        )}
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-4 gap-px bg-zinc-800">
        {statItems.map((item, i) => (
          <div key={i} className="bg-zinc-900 px-3 py-3 text-center">
            <item.icon className="w-3.5 h-3.5 mx-auto mb-1" style={{ color: item.color }} />
            <p className="text-lg font-bold text-white leading-none">{item.value}</p>
            <p className="text-[10px] text-zinc-500 mt-0.5">{item.label}</p>
          </div>
        ))}
      </div>

      {/* Patterns */}
      {(patterns.favorite_genres?.length > 0 || patterns.peak_reading_hours?.length > 0) && (
        <div className="px-5 py-3 border-t border-zinc-800 flex flex-wrap gap-3">
          {patterns.favorite_genres?.length > 0 && (
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] text-zinc-500">Top genres:</span>
              {patterns.favorite_genres.slice(0, 3).map(g => (
                <span key={g} className="text-[10px] px-1.5 py-0.5 bg-zinc-800 rounded text-zinc-300">{g}</span>
              ))}
            </div>
          )}
          {patterns.peak_reading_hours?.length > 0 && (
            <div className="flex items-center gap-1.5">
              <Clock className="w-3 h-3 text-zinc-500" />
              <span className="text-[10px] text-zinc-500">Peak:</span>
              <span className="text-[10px] text-zinc-300">
                {patterns.peak_reading_hours.map(h => `${h}:00`).join(', ')}
              </span>
            </div>
          )}
        </div>
      )}

      {/* Engagement Progress */}
      <div className="px-5 py-3 border-t border-zinc-800">
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-[10px] text-zinc-500">Engagement Level</span>
          <span className="text-[10px] font-medium" style={{ color: typeConfig.color }}>
            {stats.avg_engagement_score}%
          </span>
        </div>
        <div className="h-1.5 bg-zinc-800 rounded-full overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-1000"
            style={{
              width: `${Math.min(stats.avg_engagement_score, 100)}%`,
              backgroundColor: typeConfig.color
            }}
          />
        </div>
      </div>
    </div>
  );
}
